/**
 * AutoSummarizeToggle Component (AC-1161)
 *
 * Toggle for enabling/disabling auto-summarize on a per-workflow basis.
 * When enabled, the runner generates a human-readable summary after each run.
 */

import React, { useState } from 'react';
import { FileText, Loader } from 'lucide-react';
import apiService from '../../services/api';

interface AutoSummarizeToggleProps {
  orchestrationId: number;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

const AutoSummarizeToggle: React.FC<AutoSummarizeToggleProps> = ({
  orchestrationId,
  enabled,
  onToggle,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    const newValue = !enabled;
    try {
      setIsLoading(true);
      setError(null);
      await apiService.updateWorkflow(String(orchestrationId), {
        auto_summarize: newValue,
        skip_version_control: true,
      });
      onToggle(newValue);
    } catch (err: any) {
      console.error('[AutoSummarizeToggle] Toggle failed:', err);
      setError('Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center min-w-0 mr-3">
        <FileText className="w-4 h-4 text-purple-600 dark:text-purple-500 mr-2 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Auto-Summarize
          </p>
          {error && (
            <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
          )}
        </div>
      </div>
      <button
        role="switch"
        aria-checked={enabled}
        aria-label="Auto-Summarize"
        disabled={isLoading}
        onClick={handleToggle}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${enabled ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'}`}
      >
        {isLoading ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <Loader className="w-3 h-3 text-white animate-spin" />
          </span>
        ) : (
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        )}
      </button>
    </div>
  );
};

export default AutoSummarizeToggle;
